import React, { useState, useEffect } from "react";
import {
  Card,
  Select,
  DatePicker,
  Input,
  Button,
  Tag,
  message,
  Spin,
  Tooltip,
  Modal
} from "antd";
import {
  MessageOutlined,
  CloseOutlined,
  DownOutlined
} from "@ant-design/icons";
import moment from "moment";
import Fetch from "../../utils/fetch";
import fetchCached, { SHORT_TTL } from "../../utils/cachedFetch";
import { baseURL } from "../../config";
import axios from "axios";
import GenericPopup from "../../components/genericPopup";
import styles from "./index.module.scss";
import jwtDecode from "jwt-decode";
import IntraIcon from "../../images/intra.svg";
import EditedIcon from "../../images/edited.svg";

const { Option } = Select;
const { RangePicker } = DatePicker;

const downloadPDF = async (path, options = {}, isArrayBuffer = false) => {
  try {
    const token = localStorage.getItem("idToken");
    if (!token) {
      throw new Error("No JWT token found");
    }

    const headers = {
      "Content-Type": "application/json",
      ...options.headers
    };

    if (token) {
      headers.authorization = `Bearer ${token}`;
    }

    const response = await axios({
      method: options.method || "POST",
      url: `${baseURL}${path}`,
      ...options,
      headers,
      responseType: isArrayBuffer ? "arraybuffer" : "json"
    });

    if (isArrayBuffer) {
      if (response.data && response.data.byteLength > 0) {
        return response.data;
      } else {
        return { statusCode: "error", message: "Empty or invalid PDF data" };
      }
    }

    const { data, statusCode } = response.data;
    if (statusCode === "ok") {
      return data;
    } else {
      if (data == "Sorry! You're not allowed" && statusCode == "Not Allowed") {
        message.error(JSON.stringify(data));
        return { statusCode: "error", message: data };
      } else if (data !== null && statusCode !== "Booking Not Found") {
        message.error(JSON.stringify(data));
        return { statusCode: "error", message: data };
      }
      return { statusCode: "error", message: "Unknown error occurred" };
    }
  } catch (err) {
    if (err.response) {
      if (isArrayBuffer && err.response.data instanceof ArrayBuffer) {
        const decoder = new TextDecoder();
        const errorText = decoder.decode(err.response.data);
        try {
          const errorJson = JSON.parse(errorText);
          message.error(errorJson.data || "Error loading PDF");
          return {
            statusCode: "error",
            message: errorJson.data || "Error loading PDF"
          };
        } catch (parseErr) {
          message.error("Failed to load PDF");
          return {
            statusCode: "error",
            message: errorText || "Failed to load PDF"
          };
        }
      }

      const { data, statusCode } = err.response.data || {};
      switch (statusCode) {
        case "error":
          message.error(JSON.stringify(data));
          return { statusCode: "error", message: data };

        case "Not Allowed":
          message.error(data || "Not Allowed");
          return { statusCode: "error", message: data || "Not Allowed" };

        case "Not Found":
          message.error("Not Found");
          return { statusCode: "error", message: "Not Found" };

        default:
          message.error("Something went wrong");
          return { statusCode: "error", message: "Something went wrong" };
      }
    } else {
      message.error("Something went wrong");
      return {
        statusCode: "error",
        message: err.message || "Something went wrong"
      };
    }
  }
};

const RegionalBillingManagement = () => {
  const currentWeekStart = moment().startOf("isoWeek"); // Monday
  const currentWeekEnd = moment().endOf("isoWeek"); // Sunday

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [dateRange, setDateRange] = useState([
    currentWeekStart,
    currentWeekEnd
  ]);
  const [operatorName, setOperatorName] = useState(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [invoicesData, setInvoicesData] = useState([]);
  const [cabOperators, setOperators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [modalType, setModalType] = useState("view");
  const [currentInvoiceId, setCurrentInvoiceId] = useState(null);
  const [loadingViewInvoiceId, setLoadingViewInvoiceId] = useState(null);
  const [loadingActionInvoiceId, setLoadingActionInvoiceId] = useState(null);
  const [acceptConfirmVisible, setAcceptConfirmVisible] = useState(false);
  const [rejectConfirmVisible, setRejectConfirmVisible] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [successPopupVisible, setSuccessPopupVisible] = useState(false);
  const [failurePopupVisible, setFailurePopupVisible] = useState(false);
  const [commentPopupVisible, setCommentPopupVisible] = useState(false);
  const [currentComment, setCurrentComment] = useState(null);

  const [logsPopupVisible, setLogsPopupVisible] = useState(false);
  const [logsParts, setLogsParts] = useState([]);
  const [collapsedCards, setCollapsedCards] = useState({});
  const token = localStorage.getItem("idToken");
  const decodedToken = jwtDecode(token);
  const isAdmin =
    (decodedToken &&
      decodedToken.privileges &&
      decodedToken.privileges.includes("last_mile_admin")) ||
    false;

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const startDate = moment()
        .startOf("week")
        .valueOf();
      const endDate = moment()
        .endOf("week")
        .valueOf();
      const url = `/cabOperator/searchInvoices?fromDate=${startDate}&toDate=${endDate}`;
      const options = {
        method: "GET"
      };

      const response = await Fetch(url, options);

      if (response && Array.isArray(response)) {
        setInvoicesData(response);
        setFilteredData(response);
      } else {
        setInvoicesData([]);
        setFilteredData([]);
      }
    } catch (error) {
      message.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchOperators = async () => {
    try {
      const url = `/cabOperator/getOperator`;

      const response = await fetchCached(url, SHORT_TTL);
      if (response && Array.isArray(response)) {
        if (!isAdmin) {
          const filteredOperators = response.filter(
            operator => operator.regionalManagerId === decodedToken.id
          );
          setOperators(filteredOperators);
        } else {
          setOperators(response);
        }
      } else {
        message.warning("No Operator found");
      }
      return response;
    } catch (error) {
      message.error("Failed to fetch operators");
    }
  };

  useEffect(() => {
    fetchInvoices();
    fetchOperators();
  }, []);

  const handleStatusChange = value => {
    setSelectedStatus(value);
  };

  const handleDateChange = dates => {
    setDateRange(dates);
  };

  const handleOperatorNameChange = (value, data) => {
    if (value === null || value === undefined) {
      setSelectedOperatorId(null);
      setOperatorName(null);
    } else {
      const key = data.key || null;
      setSelectedOperatorId(key);
      setOperatorName(value);
    }
  };

  const handleCloseLogsPopup = () => {
    setLogsPopupVisible(false);
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      let url = `/cabOperator/searchInvoices?`;

      let startDate = moment()
        .startOf("week")
        .valueOf();
      let endDate = moment()
        .endOf("week")
        .valueOf();
      if (dateRange && dateRange.length === 2) {
        startDate = moment(dateRange[0])
          .startOf("day")
          .valueOf();
        endDate = moment(dateRange[1])
          .endOf("day")
          .valueOf();
      }
      url += `fromDate=${startDate}&toDate=${endDate}`;

      if (selectedStatus) {
        url += `&status=${selectedStatus}`;
      }

      if (selectedOperatorId) {
        url += `&cabOperatorId=${selectedOperatorId}`;
      }
      const options = {
        method: "GET"
      };

      const response = await Fetch(url, options);

      if (response && Array.isArray(response)) {
        setInvoicesData(response);
        setFilteredData(response);
      } else {
        setInvoicesData([]);
        setFilteredData([]);
      }
    } catch (error) {
      message.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (invoiceId, type = "view") => {
    try {
      if (type === "view") {
        setLoadingViewInvoiceId(invoiceId);
      } else {
        setLoadingActionInvoiceId(invoiceId);
      }
      setModalType(type);
      setCurrentInvoiceId(invoiceId);

      const url = `/cabOperator/donwloadInvoice`;
      const options = {
        method: "POST",
        data: {
          invoiceId: invoiceId,
          isDownload: false
        }
      };

      const pdfData = await downloadPDF(url, options, true);

      if (pdfData && pdfData.statusCode === "error") {
        message.error(pdfData.message || "Failed to load PDF invoice");
        return;
      }

      if (pdfData && (pdfData instanceof ArrayBuffer || pdfData.byteLength)) {
        const blob = new Blob([pdfData], { type: "application/pdf" });
        const blobUrl = window.URL.createObjectURL(blob);

        setPdfUrl(blobUrl);
        setPdfModalVisible(true);
        message.success("Invoice loaded successfully");
      } else {
        message.error("Failed to load PDF invoice - Invalid data");
      }
    } catch (error) {
      message.error("Failed to view invoice");
    } finally {
      if (type === "view") {
        setLoadingViewInvoiceId(null);
      } else {
        setLoadingActionInvoiceId(null);
      }
    }
  };

  const handleClosePdfModal = () => {
    setPdfModalVisible(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!currentInvoiceId) return;

    try {
      setPdfLoading(true);
      const url = `/cabOperator/donwloadInvoice`;
      const options = {
        method: "POST",
        data: {
          invoiceId: currentInvoiceId,
          isDownload: true
        }
      };

      const pdfData = await downloadPDF(url, options, true);

      if (pdfData && pdfData.statusCode === "error") {
        message.error(pdfData.message || "Failed to download PDF invoice");
        return;
      }

      if (pdfData && (pdfData instanceof ArrayBuffer || pdfData.byteLength)) {
        const blob = new Blob([pdfData], { type: "application/pdf" });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `Invoice_${currentInvoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
        message.success("Invoice downloaded successfully");
      } else {
        message.error("Failed to download PDF invoice - Invalid data");
      }
    } catch (error) {
      message.error("Failed to download invoice");
    } finally {
      setPdfLoading(false);
    }
  };

  const showLogs = _id => {
    const invoice = invoicesData.find(data => data._id === _id);
    const logs = invoice ? invoice.remark : "";

    if (logs) {
      const statusParts = logs.split(" || ").map(s => s.trim());

      const movedWithRemarkRegex = /^(.+?)\s+moved from\s+(.+?)\s+to\s+(.+?)\s+by\s+(.+?)\s+at\s+(.+?)\s+with remark\s+(.+)$/i;
      const movedRegex = /^(.+?)\s+moved from\s+(.+?)\s+to\s+(.+?)\s+by\s+(.+?)\s+at\s+(.+)$/i;
      const addedRegex = /^(.+?)\s+added by\s+(.+?)\s+at\s+(.+)$/i;
      const updatedRegex = /^(.+?)\s+updated to\s+(.+?)\s+by\s+(.+)$/i;
      const genericRegex = /^(.+?)\s+by\s+(.+?)(?:\s+at\s+(.+))?$/i;

      const parts = statusParts
        .map(status => {
          let match;
          match = status.match(movedWithRemarkRegex);
          if (match) {
            return {
              value: match[1].trim(),
              oldValue: match[2].trim(),
              newValue: match[3].trim(),
              actor: match[4].trim(),
              time: match[5] ? match[5].trim() : "NA",
              remark: match[6] ? match[6].trim() : ""
            };
          }
          match = status.match(movedRegex);
          if (match) {
            return {
              value: match[1].trim(),
              oldValue: match[2].trim(),
              newValue: match[3].trim(),
              actor: match[4].trim(),
              time: match[5] ? match[5].trim() : "NA",
              remark: ""
            };
          }
          match = status.match(addedRegex);
          if (match) {
            return {
              value: match[1].trim(),
              oldValue: "NA",
              newValue: "Added",
              actor: match[2].trim(),
              time: match[3] ? match[3].trim() : "NA",
              remark: ""
            };
          }
          match = status.match(updatedRegex);
          if (match) {
            return {
              value: match[1].trim(),
              oldValue: "NA",
              newValue: match[2].trim(),
              actor: match[3].trim(),
              time: "NA",
              remark: ""
            };
          }
          match = status.match(genericRegex);
          if (match) {
            return {
              value: match[1].trim(),
              oldValue: "NA",
              newValue: "",
              actor: match[2].trim(),
              time: match[3] ? match[3].trim() : "NA",
              remark: ""
            };
          }

          return null;
        })
        .filter(item => item !== null);

      setLogsParts(parts);
      setLogsPopupVisible(true);
    } else {
      message.error("No matching logs found");
    }
  };

  const updateInvoice = async (invoiceId, status, comment = "") => {
    try {
      const currentInvoice = invoicesData.find(inv => inv._id === invoiceId);
      if (!currentInvoice) {
        message.error("Could not find the invoice to update.");
        if (status === "APPROVED") {
          setAcceptLoading(false);
        } else if (status === "REJECTED") {
          setRejectLoading(false);
        }
        return;
      }

      const currentStage = currentInvoice.stage;
      const privileges =
        decodedToken && decodedToken.privileges ? decodedToken.privileges : [];

      let newStage = "";
      let newStatus = "";

      if (
        currentStage === "PENDING_BY_RM" &&
        privileges.includes("last_mile_manager")
      ) {
        if (status === "APPROVED") {
          newStage = "PENDING_BY_ADMIN";
          newStatus = "PENDING";
        } else if (status === "REJECTED") {
          newStage = "RM_REJECTED";
          newStatus = "REJECTED";
        }
      } else if (
        currentStage === "PENDING_BY_ADMIN" &&
        privileges.includes("last_mile_admin")
      ) {
        if (status === "APPROVED") {
          newStage = "ADMIN_APPROVED";
          newStatus = "APPROVED";
        } else if (status === "REJECTED") {
          newStage = "ADMIN_REJECTED";
          newStatus = "REJECTED";
        }
      }

      if (!newStage || !newStatus) {
        message.error(
          "Action cannot be performed for the current invoice stage or user role."
        );
        if (status === "APPROVED") {
          setAcceptLoading(false);
        } else if (status === "REJECTED") {
          setRejectLoading(false);
        }
        return;
      }
      const existingRemark =
        currentInvoice && currentInvoice.remark ? currentInvoice.remark : "";
      const currentTime = moment().format("DD-MM-YYYY HH:mm");
      const rejectionRemark = comment;
      const remarkSuffix =
        status === "REJECTED" && rejectionRemark
          ? ` with remark ${rejectionRemark}`
          : "";
      const newRemark = `stage moved from ${currentStage} to ${newStage} by ${decodedToken.name} at ${currentTime}${remarkSuffix}`;
      const finalRemark = existingRemark
        ? `${existingRemark} || ${newRemark}`
        : newRemark;

      const url = `/cabOperator/updateInvoice`;
      const options = {
        method: "PUT",
        data: {
          invoiceId: invoiceId,
          status: newStatus,
          stage: newStage,
          comment: comment,
          remark: finalRemark
        }
      };

      const response = await Fetch(url, options);

      if (response && Array.isArray(response) && response.length > 0) {
        setAcceptConfirmVisible(false);
        setRejectConfirmVisible(false);
        setRejectComment("");
        setSuccessPopupVisible(true);
        fetchInvoices();
      } else {
        setAcceptConfirmVisible(false);
        setRejectConfirmVisible(false);
        setRejectComment("");
        setFailurePopupVisible(true);
        fetchInvoices();
      }
    } catch (error) {
      setAcceptConfirmVisible(false);
      setRejectConfirmVisible(false);
      setRejectComment("");
      setFailurePopupVisible(true);
      fetchInvoices();
    } finally {
      if (status === "APPROVED") {
        setAcceptLoading(false);
      } else if (status === "REJECTED") {
        setRejectLoading(false);
      }
      setAcceptConfirmVisible(false);
      setRejectConfirmVisible(false);
      setRejectComment("");
      handleClosePdfModal();
    }
  };

  const handleAcceptInvoice = () => {
    setAcceptConfirmVisible(true);
  };

  const handleRejectInvoice = () => {
    setRejectConfirmVisible(true);
  };

  const handleAcceptConfirm = () => {
    setAcceptLoading(true);
    updateInvoice(currentInvoiceId, "APPROVED");
    setAcceptConfirmVisible(false);
  };

  const handleRejectConfirm = () => {
    setRejectLoading(true);
    updateInvoice(currentInvoiceId, "REJECTED", rejectComment);
    setRejectConfirmVisible(false);
    setRejectComment("");
  };

  const handleRejectCommentChange = e => {
    setRejectComment(e.target.value);
  };

  const handleOpenCommentPopup = invoice => {
    setCurrentComment(invoice.comment || "");
    setCommentPopupVisible(true);
  };

  const handleCloseCommentPopup = () => {
    setCommentPopupVisible(false);
  };

  const getStatusColor = status => {
    switch (status) {
      case "PENDING":
        return "orange";
      case "COMPLETED":
        return "green";
      case "APPROVED":
        return "blue";
      case "REJECTED":
        return "red";
      default:
        return "default";
    }
  };

  const toggleCardCollapse = invoiceId => {
    setCollapsedCards(prev => ({
      ...prev,
      [invoiceId]: !prev[invoiceId]
    }));
  };

  const calculateTotals = invoice => {
    const baseAmount =
      (invoice.cabBookingGroups && invoice.cabBookingGroups.length > 0
        ? invoice.cabBookingGroups.reduce((sum, group) => {
            const supplyCost = group.supplyCost || 0;
            return sum + (isNaN(supplyCost) ? 0 : supplyCost);
          }, 0)
        : 0) || 0;

    // const gstRate = invoice.cabOperator?.gstRate || 0;
    const gstRate = invoice.cabOperator.isRcm
      ? 0
      : invoice.cabOperator && invoice.cabOperator.gstRate
      ? invoice.cabOperator.gstRate
      : 0;
    const totalAmountExcludingGST = baseAmount / (1 + gstRate / 100);
    let totalGST = baseAmount - totalAmountExcludingGST;
    totalGST = Number(totalGST.toFixed(2));

    const totalAmount =
      isNaN(baseAmount) || isNaN(totalGST) ? 0 : baseAmount + totalGST;
    return {
      totalAmount: isNaN(totalAmount) ? 0 : totalAmount,
      totalGST: isNaN(totalGST) ? 0 : totalGST,
      grandTotal: isNaN(totalAmount) ? 0 : totalAmount
    };
  };

  const renderBillingCard = invoice => {
    const { stage } = invoice;
    const privileges =
      decodedToken && decodedToken.privileges ? decodedToken.privileges : [];
    let isPresent = false;
    if (stage === "PENDING_BY_RM" && privileges.includes("last_mile_manager")) {
      isPresent = true;
    } else if (
      stage === "PENDING_BY_ADMIN" &&
      privileges.includes("last_mile_admin")
    ) {
      isPresent = true;
    }
    const totals = calculateTotals(invoice);
    const isCollapsed = collapsedCards[invoice._id];
    return (
      <Card key={invoice._id} className={styles.billingCard}>
        <div className={styles.cardHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.invoiceSection}>
              <span className={styles.invoiceLabel}>Invoice Number:</span>
              <span className={styles.invoiceNumber}>
                {invoice.invoiceNumber}
              </span>
              <Tag
                color={getStatusColor(invoice.status)}
                className={styles.statusTag}
              >
                {invoice.status}
              </Tag>
              <Tag className={styles.statusByTag}>
                {invoice.stage ? invoice.stage.replaceAll("_", " ") : ""}
              </Tag>
            </div>
          </div>
          <div className={styles.headerRight}>
            <span className={styles.operatorLabel}>Operator Name:</span>
            <span className={styles.operatorName}>
              {invoice.cabOperator ? invoice.cabOperator.name : ""}
            </span>
            <DownOutlined
              className={`${styles.collapseIcon} ${
                isCollapsed ? styles.collapsed : ""
              }`}
              onClick={() => toggleCardCollapse(invoice._id)}
            />
          </div>
        </div>

        {!isCollapsed && (
          <div className={styles.cardContent}>
            <div className={styles.tableContainer}>
              <table className={styles.billingTable}>
                <thead>
                  <tr>
                    <th className={styles.routeColumn}>Route</th>
                    <th className={styles.timeColumn}>Start Time</th>
                    <th className={styles.dateColumn}>Date</th>
                    <th className={styles.tripColumn}>Trip ID</th>
                    <th className={styles.assetColumn}>Asset Type</th>
                    <th className={styles.passengerColumn}>Passengers</th>
                    <th className={styles.taxColumn}>Tax</th>
                    <th className={styles.amountColumn}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.cabBookingGroups &&
                    invoice.cabBookingGroups.map((group, index) => {
                      const route = `${
                        group.fromCity && group.fromCity.name
                          ? group.fromCity.name
                          : ""
                      } to ${
                        group.toCity && group.toCity.name
                          ? group.toCity.name
                          : ""
                      }`;

                      const supplyCost = group.supplyCost || 0;
                      const gstRate =
                        invoice.cabOperator && !invoice.cabOperator.isRcm
                          ? invoice.cabOperator.gstRate || 0
                          : 0;

                      const totalAmountExcludingGST =
                        supplyCost / (1 + gstRate / 100);
                      let tax = supplyCost - totalAmountExcludingGST;
                      tax = Number(tax.toFixed(2));

                      const isIntraCity = group.isIntraCity;
                      const isEdited =
                        group.lastModifiedOn >
                        Math.max(
                          invoice.lastRejectedOn,
                          group.attachedOnInvoiceAt
                        );

                      return (
                        <tr
                          key={index}
                          className={
                            index % 2 === 0 ? styles.evenRow : styles.oddRow
                          }
                        >
                          <td className={styles.routeColumn}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                              }}
                            >
                              <span>{route || "-"}</span>
                              {isIntraCity && (
                                <Tooltip
                                  title={`${
                                    group.fromCity && group.fromCity.name
                                      ? group.fromCity.name
                                      : ""
                                  } - ${
                                    group.toCity && group.toCity.name
                                      ? group.toCity.name
                                      : ""
                                  }`}
                                >
                                  <span className={styles.intraTag}>
                                    <img
                                      src={IntraIcon}
                                      alt="Intra"
                                      className={styles.tagIcon}
                                    />
                                    Intra
                                  </span>
                                </Tooltip>
                              )}
                              {isEdited && (
                                <span className={styles.editedTag}>
                                  <img
                                    src={EditedIcon}
                                    alt="Edited"
                                    className={styles.tagIcon}
                                  />
                                  Edited
                                </span>
                              )}
                            </div>
                          </td>
                          <td className={styles.timeColumn}>
                            {moment(group.startTime).format("HH:mm A") || "-"}
                          </td>
                          <td className={styles.dateColumn}>
                            {moment(group.startTime).format("YYYY-MM-DD") ||
                              "-"}
                          </td>
                          <td className={styles.tripColumn}>
                            {group.groupCode || "-"}
                          </td>
                          <td className={styles.assetColumn}>
                            {group.assetType || "-"}
                          </td>
                          <td className={styles.passengerColumn}>
                            {group.totalPassengers || 0}
                          </td>
                          <td className={styles.taxColumn}>
                            GST ₹{isNaN(tax) ? 0 : tax.toFixed(2)}
                          </td>
                          <td className={styles.amountColumn}>
                            ₹{group.supplyCost || 0}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className={styles.cardFooter}>
          <div className={styles.totals}>
            <div className={styles.totalRow}>
              <span>
                Total GST: ₹
                {isNaN(totals.totalGST) ? 0 : totals.totalGST.toFixed(2)}
              </span>
              <span className={styles.totalAmount}>
                Total Amount: ₹
                {isNaN(totals.grandTotal)
                  ? 0
                  : (totals.grandTotal - totals.totalGST).toFixed(2)}
              </span>
            </div>
          </div>
          <div className={styles.actionButtons}>
            {isPresent && (
              <Button
                type="primary"
                onClick={() => handleViewInvoice(invoice._id, "action")}
                loading={loadingActionInvoiceId === invoice._id}
              >
                {loadingActionInvoiceId === invoice._id ? "" : "Take Action"}
              </Button>
            )}
            <Button onClick={() => showLogs(invoice._id)}>Logs</Button>
            <Button
              onClick={() => handleViewInvoice(invoice._id)}
              loading={loadingViewInvoiceId === invoice._id}
            >
              {loadingViewInvoiceId === invoice._id ? "" : "View Invoice"}
            </Button>
            {invoice.status == "REJECTED" && (
              <Button
                icon={<MessageOutlined />}
                onClick={() => handleOpenCommentPopup(invoice)}
              />
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className={styles.regionalBillingManagement}>
      <div className={styles.searchSection}>
        <div className={styles.auditSearch}>
          <span>{isAdmin ? "Admin " : "Regional "} Billing Management</span>
          <div className={styles.selectables}>
            <div className={styles.dateSelect}>
              <RangePicker
                allowClear
                value={dateRange}
                format="YYYY-MM-DD"
                onChange={handleDateChange}
              />
            </div>
            <Select
              allowClear
              placeholder="Choose Invoice status"
              showSearch
              style={{ width: 200 }}
              value={selectedStatus}
              onChange={handleStatusChange}
            >
              <Option value="PENDING">Pending</Option>
              <Option value="REJECTED">Rejected</Option>
              <Option value="APPROVED">Approved</Option>
            </Select>
            <Select
              allowClear
              showSearch
              placeholder="Choose operator"
              style={{ width: 450 }}
              onChange={(value, data) => handleOperatorNameChange(value, data)}
              value={operatorName}
              filterOption={(input, option) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              {cabOperators.map(operator => (
                <Option value={operator.value} key={operator._id}>
                  {operator.value}
                </Option>
              ))}
            </Select>
            <button className={styles.searchBtn} onClick={handleSearch}>
              <span>Search</span>
            </button>
            <button
              className={styles.searchBtn}
              onClick={fetchInvoices}
              disabled={loading}
              style={{ marginLeft: "10px", width: "170px" }}
            >
              <span>{loading ? "Loading..." : "Refresh Invoices"}</span>
            </button>
          </div>
        </div>
      </div>

      <div className={styles.billingCardsContainer}>
        <div className={styles.billingCardsSection}>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "400px",
                flexDirection: "column",
                gap: "16px"
              }}
            >
              <Spin size="large" />
              <p style={{ margin: 0, fontSize: "16px", color: "#666" }}>
                Loading billing data...
              </p>
            </div>
          ) : filteredData.length > 0 ? (
            filteredData.map(invoice => renderBillingCard(invoice))
          ) : (
            <Card className={styles.noDataCard}>
              <div className={styles.noData}>
                <p>No billing data found for the selected criteria.</p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {pdfModalVisible && (
        <div className="pdf-modal-overlay" onClick={handleClosePdfModal}>
          <div
            className="pdf-modal-container"
            onClick={e => e.stopPropagation()}
          >
            <div className="pdf-modal-header">
              <h3 className="pdf-modal-title">
                {modalType === "view" ? "View Invoice" : "Take Action"}
              </h3>
              <button
                onClick={handleClosePdfModal}
                className="pdf-modal-close-btn"
              >
                <CloseOutlined />
              </button>
            </div>

            <div className={`pdf-modal-content ${styles.pdfModalContent}`}>
              <button
                onClick={handleDownloadInvoice}
                className={styles.pdfModalDownloadBtn}
                disabled={pdfLoading}
                title="Download PDF"
              >
                {pdfLoading ? (
                  <>
                    <Spin size="small" style={{ marginRight: "8px" }} />
                    Downloading...
                  </>
                ) : (
                  "Download PDF"
                )}
              </button>
              {pdfUrl ? (
                <iframe
                  src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0&resize=0&view=FitH`}
                  className="pdf-modal-iframe"
                  title="Invoice PDF"
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    fontSize: "16px",
                    color: "#666"
                  }}
                >
                  Loading PDF...
                </div>
              )}
            </div>

            {modalType === "action" && (
              <div className="pdf-modal-footer">
                <Button
                  onClick={handleRejectInvoice}
                  loading={rejectLoading}
                  className="pdf-modal-btn reject"
                >
                  {rejectLoading ? "" : "Reject"}
                </Button>
                <Button
                  onClick={handleAcceptInvoice}
                  loading={acceptLoading}
                  className="pdf-modal-btn accept"
                >
                  {acceptLoading ? "" : "Accept"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* <GenericPopup
        visible={commentPopupVisible}
        onClose={handleCloseCommentPopup}
        type="question"
        title="Comment"
        message={currentComment}
        primaryButtonText="ok"
        // secondaryButtonText="Cancel"
        onPrimaryAction={handleSubmitComment}
        onSecondaryAction={handleCloseCommentPopup}
        primaryButtonDisabled={!commentText.trim()}
      ></GenericPopup> */}

      <Modal
        centered
        title={null} // hides the title bar
        visible={commentPopupVisible} // use `open` instead of `visible` in AntD v5
        closable={false} // hides the close (×) button
        footer={null} // hides footer buttons
        onCancel={handleCloseCommentPopup}
      >
        <div className={styles.modalDiv}>
          <p>Comment</p>
          <span>{currentComment}</span>
          <button
            className={styles.modalButton}
            onClick={handleCloseCommentPopup}
          >
            OK
          </button>
        </div>
      </Modal>

      <GenericPopup
        visible={acceptConfirmVisible}
        onClose={() => setAcceptConfirmVisible(false)}
        type="question"
        title="Are You Sure?"
        message="Do you want to accept this invoice?"
        primaryButtonText="Accept"
        secondaryButtonText="Cancel"
        onPrimaryAction={handleAcceptConfirm}
        onSecondaryAction={() => setAcceptConfirmVisible(false)}
      />

      <GenericPopup
        visible={rejectConfirmVisible}
        onClose={() => {
          setRejectConfirmVisible(false);
          setRejectComment("");
        }}
        type="question"
        title="Are You Sure?"
        message="Are you sure you want to reject the bill request?"
        primaryButtonText="Yes"
        secondaryButtonText="Cancel"
        onPrimaryAction={handleRejectConfirm}
        onSecondaryAction={() => {
          setRejectConfirmVisible(false);
          setRejectComment("");
        }}
        primaryButtonDanger={true}
        primaryButtonDisabled={!rejectComment.trim()}
      >
        <div style={{ marginTop: "16px" }}>
          <Input.TextArea
            placeholder="Please mention the reason"
            value={rejectComment}
            onChange={handleRejectCommentChange}
            style={{
              marginBottom: "16px",
              height: "120px",
              resize: "none"
            }}
          />
        </div>
      </GenericPopup>

      <GenericPopup
        visible={successPopupVisible}
        onClose={() => setSuccessPopupVisible(false)}
        type="success"
        title="Change Successful"
        message="You will have to reassign this trip"
        okButtonText="OK"
        onOk={() => setSuccessPopupVisible(false)}
        closable={false}
      />

      <GenericPopup
        visible={failurePopupVisible}
        onClose={() => setFailurePopupVisible(false)}
        type="error"
        title="Oops"
        message="This action cannot be performed if there is an error from the backend."
        okButtonText="OK"
        onOk={() => setFailurePopupVisible(false)}
        closable={false}
      />
      {logsPopupVisible && (
        <div className={styles.overlay} onClick={handleCloseLogsPopup}>
          <div
            className={styles.popupContainer}
            onClick={e => e.stopPropagation()}
          >
            <div className={styles.popupHeader}>
              <h3>Logs</h3>
              <button
                onClick={handleCloseLogsPopup}
                className={styles.closeButton}
              >
                <CloseOutlined />
              </button>
            </div>

            <div className={styles.popupContent}>
              <div className={styles.tableWrapper}>
                <table className={styles.logsTable}>
                  <thead>
                    <tr>
                      <th>Value</th>
                      <th>Old Value</th>
                      <th>Updated Value</th>
                      <th>Actor</th>
                      <th>Time</th>
                      <th>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsParts.map((log, index) => (
                      <tr
                        key={index}
                        className={
                          index % 2 === 0 ? styles.rowEven : styles.rowOdd
                        }
                      >
                        <td>{log.value}</td>
                        <td>{log.oldValue ? log.oldValue : "NA"}</td>
                        <td>{log.newValue}</td>
                        <td>{log.actor}</td>
                        <td>{log.time}</td>
                        <td>{log.remark ? log.remark : "NA"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className={styles.popupFooter}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegionalBillingManagement;
